import { Timeseries } from "../types.ts";
import { average } from "./utils.ts";
import { computeAnomalies, AnomalyType, AnomalyRange } from "./anomalies.ts";

export type { AnomalyType, AnomalyRange };

function movingRange(vs: (number | null)[]): (number | null)[] {
  const res: (number | null)[] = [null];
  for (let i = 1; i < vs.length; i++) {
    // Skip if either value is null
    if (vs[i] == null || vs[i - 1] == null) {
      res.push(null);
    } else {
      res.push(Math.abs(vs[i] - vs[i - 1]));
    }
  }
  return res;
}

export interface Xmr {
  ts: Timeseries;
  movingRange: number[];
  avgX: number;
  avgMR: number;
  upperNaturalProcessLimit: number;
  lowerNaturalProcessLimit: number;
  upperRangeLimit: number;
  sigma: number;
  anomalies: AnomalyRange[];
}

export interface ComputeXmrOptions {
  transform?: "identity" | "log";
}

export function computeXmr(ts: Timeseries, options?: ComputeXmrOptions): Xmr {
  const transform = options?.transform ?? "identity";
  const workingTs: Timeseries =
    transform === "log"
      ? {
          timestamps: ts.timestamps,
          data: ts.data.map(value => (value == null ? null : Math.log(Math.max(value, 1e-9)))),
        }
      : ts;

  const mr = movingRange(workingTs.data);
  const avgX = average(workingTs.data);
  const avgMR = average(mr.slice(1));

  let unpl: number;
  let lnpl: number;
  let sigma: number;

  if (transform === "log") {
    sigma = avgMR / 1.128;
    unpl = avgX + 3 * sigma;
    lnpl = avgX - 3 * sigma;
  } else {
    unpl = avgX + 2.66 * avgMR;
    lnpl = avgX - 2.66 * avgMR;
    sigma = (2.66 / 3) * avgMR;
  }

  const url = 3.268 * avgMR;

  const supportedAnomalyTypes: AnomalyType[] = ["outside_limits", "two_sigma", "run_of_eight"];
  const anomalies = computeAnomalies(
    supportedAnomalyTypes,
    workingTs.timestamps,
    workingTs.data,
    avgX,
    sigma,
    lnpl,
    unpl,
  );

  return {
    ts: workingTs,
    movingRange: mr,
    avgX,
    avgMR,
    upperNaturalProcessLimit: unpl,
    lowerNaturalProcessLimit: lnpl,
    upperRangeLimit: url,
    sigma,
    anomalies,
  };
}
